import { fromJS, List} from 'immutable';
import { clone, filter } from 'lodash';
import * as categoriesActionTypes from '../actions/categoriesActionTypes';
import * as umoActionTypes from '../actions/umoActionTypes';
import { getKeyPath } from '../utils/immutableUtils';
import appConfig from '../appConfig';

const initialState = fromJS({
  categoryList: [],
  isRetrievingRoot: false,
  rootRequest: null,
  retrieveChildrenRequests: {},
  createRequests: [],
  deleteRequests: {},
  editRequests: {},
  moveRequests: {},
});

const createCategoryData = (id, name, description) => (fromJS({
  n: name,
  d: description,
  t: 'C',
  i: id,
  x: '0',
  opk: '0',
  ch: 'FALSE',
  u: '',
  ct: [],
  xpnd: false, // isExpanded- used to remember your category tree location
}));

const categories = (state = initialState, action) => {
  switch (action.type) {
    case categoriesActionTypes.CATEGORIES_RETRIEVE_REQUEST:
      if (action.categoryID) {
        // request is for a child category, not root
        if (state.getIn(['retrieveChildrenRequests', action.categoryID]) &&
          !(state.getIn(['retrieveChildrenRequests', action.categoryID]) instanceof Error)) {
          // request already exists for this so cancel the current one
          state.getIn(['retrieveChildrenRequests', action.categoryID]).cancel();
        }

        return state
          .mergeDeep(fromJS({ retrieveChildrenRequests: { [action.categoryID]: action.requestPromise } }))
          ;
      } else if (state.get('rootRequest')) {
        // cancel the current root request promise since it exists
        state.get('rootRequest').cancel();
      }

      return state
        .set('isRetrievingRoot', true)
        .set('rootRequest', action.requestPromise)
        ;
    case categoriesActionTypes.CATEGORIES_RETRIEVE_SUCCESS: {
      // remove the lightboxes from the category list
      let processedCategoryList;
      if (action.categoryList.ct) { // normal categories have been returned
        processedCategoryList = filter(action.categoryList.ct, data => data.t !== 'L');
      } else if (action.categoryList.cl) { // categories returned with collections
        processedCategoryList = filter(action.categoryList.cl, data => data.t !== 'LX');
      }

      if (action.categoryID) {
        // request is for a child category, not root
        let newCategoryList;
        const retrieveChildrenRequests = state.get('retrieveChildrenRequests')
          .delete(action.categoryID);
        const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');

        if (foundPath) {
          newCategoryList = state
            .get('categoryList')
            .setIn([...foundPath, 'ct'], fromJS(processedCategoryList))
            .setIn([...foundPath, 'xpnd'], true);
        }

        return state
          .set('categoryList', newCategoryList || state.get('categoryList'))
          .set('retrieveChildrenRequests', retrieveChildrenRequests)
          ;
      }
      processedCategoryList.forEach((item) => { item.xpnd = !appConfig.collapseCategories; });

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', null)
        .set('categoryList', fromJS(processedCategoryList))
        .set('retrieveChildrenRequests', fromJS({}))
        ;
    }
    case categoriesActionTypes.CATEGORIES_RETRIEVE_FAILURE:
      if (action.categoryID) {
        return state
          .setIn(['retrieveChildrenRequests', action.categoryID], action.err)
          ;
      }

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', action.err)
        ;
    case categoriesActionTypes.CATEGORIES_CANCEL_ALL_REQUESTS:

      state.get('retrieveChildrenRequests').find((value) => {
        if (value.cancel) {
          value.cancel();
        }
        return false;
      });

      if (state.get('rootRequest') && state.get('rootRequest').cancel) {
        state.get('rootRequest').cancel();
      }

      return state
        .set('isRetrievingRoot', false)
        .set('rootRequest', null)
        .set('retrieveChildrenRequests', fromJS({}))
        ;
    case categoriesActionTypes.CATEGORIES_CREATE_REQUEST: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('categoryName') === action.categoryName &&
        createRequest.get('categoryParentID') === action.categoryParentID
      );

      if (foundRequestIndex !== undefined) {
        // request with same details already exist, so just overwrite that one
        return state
          .setIn(['createRequests', foundRequestIndex, 'request'], action.requestPromise)
          .setIn(['createRequests', foundRequestIndex, 'categoryDescription'],
            action.categoryDescription)
          ;
      }

      return state.set('createRequests', state.get('createRequests').push(fromJS({
        categoryName: action.categoryName,
        categoryParentID: action.categoryParentID,
        categoryDescription: action.categoryDescription,
        request: action.requestPromise,
      })));
    }
    case categoriesActionTypes.CATEGORIES_CREATE_SUCCESS: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('categoryName') === action.categoryName &&
        createRequest.get('categoryParentID') === action.categoryParentID
      );

      let newState = state;

      if (foundRequestIndex !== undefined) {
        newState = newState.deleteIn(['createRequests', foundRequestIndex]);
      }

      if (action.categoryParentID && action.categoryParentID !== '0') {
        const foundKeyPath = getKeyPath(state.get('categoryList'), 'i', action.categoryParentID, 'ct');

        // adds new category to a non-root level category
        if (foundKeyPath) {
          let newChildList = newState.getIn(['categoryList', ...foundKeyPath, 'ct']);

          if (newChildList) {
            newChildList = newChildList.push(createCategoryData(
              action.categoryID,
              action.categoryName,
              action.categoryDescription
            ));
          } else {
            newChildList = new List([createCategoryData(
              action.categoryID,
              action.categoryName,
              action.categoryDescription
            )]);
          }

          return newState
            .setIn(['categoryList', ...foundKeyPath, 'ch'], 'TRUE')
            .setIn(['categoryList', ...foundKeyPath, 'ct'], newChildList);
        }

        return newState;
      }

      // adds new category to root level category
      return newState.set('categoryList', state.get('categoryList')
        .push(createCategoryData(
          action.categoryID,
          action.categoryName,
          action.categoryDescription
        )));
    }
    case categoriesActionTypes.CATEGORIES_CREATE_FAILURE: {
      const foundRequestIndex = state.get('createRequests').findKey(createRequest =>
        createRequest.get('categoryName') === action.categoryName &&
        createRequest.get('categoryParentID') === action.categoryParentID
      );

      if (foundRequestIndex !== undefined) {
        return state.setIn(['createRequests', foundRequestIndex, 'request'], action.err);
      }

      return state;
    }
    case categoriesActionTypes.CATEGORIES_EDIT_REQUEST:
      return state.setIn(['editRequests', action.categoryID], fromJS({
        categoryName: action.categoryName,
        categoryDescription: action.categoryDescription,
        request: action.requestPromise,
      }));
    case categoriesActionTypes.CATEGORIES_EDIT_SUCCESS:
      if (state.getIn(['editRequests', action.categoryID])) {
        let newState = state.deleteIn(['editRequests', action.categoryID]);

        if (action.categoryName || action.categoryDescription) {
          const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');
          if (foundPath) {
            if (action.categoryName) {
              newState = newState.setIn(['categoryList', ...foundPath, 'n'], action.categoryName);
            }
            if (action.categoryDescription) {
              newState = newState.setIn(['categoryList', ...foundPath, 'd'], action.categoryDescription);
            }
          }
        }

        return newState;
      }
      return state;
    case categoriesActionTypes.CATEGORIES_EDIT_FAILURE:
      return state.setIn(['editRequests', action.categoryID, 'request'], action.err);
    case categoriesActionTypes.CATEGORIES_DELETE_REQUEST: {
      const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');
      if (foundPath) {
        const categoryData = state.getIn(['categoryList', ...foundPath]);
        return state.setIn(['deleteRequests', action.categoryID], fromJS({
          categoryName: categoryData.get('n'),
          categoryDescription: categoryData.get('d'),
          request: action.requestPromise,
        }));
      }

      return state;
    }
    case categoriesActionTypes.CATEGORIES_DELETE_SUCCESS: {
      const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');
      return state
        .deleteIn(['deleteRequests', action.categoryID])
        .deleteIn(['categoryList', ...foundPath])
        ;
    }
    case categoriesActionTypes.CATEGORIES_DELETE_FAILURE:
      return state.setIn(['deleteRequests', action.categoryID, 'request'], action.err);
    case categoriesActionTypes.CATEGORY_COLLAPSE:
      return state.setIn(['deleteRequests', action.categoryID, 'request'], action.err);
    case categoriesActionTypes.CATEGORIES_LIST_CLEAR: {
      let newCategoryList = state.get('categoryList');
      const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');

      if (foundPath) {
        newCategoryList = state
          .get('categoryList')
          .setIn([...foundPath, 'ct'], new List())
          .setIn([...foundPath, 'xpnd'], false);
      }
      return state
        .set('categoryList', newCategoryList)
        ;
    }
    case categoriesActionTypes.COLLECTION_COLLAPSE_TOGGLE: {
      let newCategoryList = state.get('categoryList');
      const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');

      if (foundPath) {
        const curState = state.getIn(['categoryList', ...foundPath, 'xpnd']);
        newCategoryList = state
          .get('categoryList')
          .setIn([...foundPath, 'xpnd'], !curState);
      }
      return state.set('categoryList', newCategoryList);
    }
    case categoriesActionTypes.CATEGORIES_NUMBER_INDICATOR_UPDATE: {
      let newCategoryList = state.get('categoryList');
      const foundPath = getKeyPath(state.get('categoryList'), 'i', action.categoryID, 'ct');
      if (foundPath) {
        newCategoryList = state
          .get('categoryList')
          .setIn([...foundPath, 'x'], action.indicator);
      }
      return state.set('categoryList', newCategoryList);
    }
    case umoActionTypes.UMO_MOVE_REQUEST:
      return state.setIn(['moveRequests', action.categoryID], fromJS({
        [action.requestID]: {
          request: action.requestPromise,
          umoList: action.umoList,
        },
      }));
    // failure will be handled via MessageDialog, so just remove the entry from state
    case umoActionTypes.UMO_MOVE_FAILURE:
    case umoActionTypes.UMO_MOVE_SUCCESS: {
      const newState = state.deleteIn(['moveRequests', action.categoryID, action.requestID]);
      let cleanState;

      // clean up the state by deleting empty category objects
      if (
        newState.getIn(['moveRequests', action.categoryID]) &&
        newState.getIn(['moveRequests', action.categoryID]).count() < 1
      ) {
        cleanState = newState.deleteIn(['moveRequests', action.categoryID]);
      } else {
        cleanState = newState;
      }
      return cleanState;
    }
    default:
      return state;
  }
};

export default categories;
